import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ClerkProvider } from '@clerk/clerk-react';
import { AnalyticsProvider, profileFromEnv, type AnalyticsMode } from '@tagadapay/analytics';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';

import App from './App.tsx';
// Self-hosted default UI font (Inter). Overridden per-brand at runtime by
// `applyBrandFontToDocument` via the `--font-sans` var. Eager (always needed),
// unlike the per-brand faces which lazy-load from the font registry.
import '@fontsource-variable/inter';
import './globals.css';
import { initI18n } from '@/config/i18n';
import { GaPageViews } from '@/components/analytics/ga-page-views';
import { initGa } from '@/lib/analytics/ga';
import { initXPixel } from '@/lib/analytics/x-pixel';
import { initCometlyPixel, persistCometTokenFromUrl } from '@/lib/analytics/cometly';
import {
  persistAcquisitionFromUrl,
  persistLinkedThumbmarkFromUrl,
} from '@/lib/utils/acquisition-tracking';
import { reportReferralClick } from '@/lib/utils/referral-click';
import {
  resolveSatellite,
  PRIMARY_APP_ORIGIN,
  partnerOrigins,
  TAGADAPAY_SATELLITE_ORIGINS,
} from '@/config/clerk-satellite';
import { resolveRedirectPartnerFromHash } from '@/config/current-partner';

initI18n();
initXPixel();
initCometlyPixel();
initGa();

// Capture first-touch acquisition (UTM params, ref, referrer, landing page)
// and the cross-domain linker mark (`?tgd_tm=`) before any redirect can
// strip the landing URL's query params.
persistAcquisitionFromUrl();
persistLinkedThumbmarkFromUrl();
persistCometTokenFromUrl();
reportReferralClick();

// Import your publishable key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Publishable Key');
}

// Product analytics (PostHog). VITE_POSTHOG_KEY is set only on Vercel
// Production (Main project) and the `dev` branch preview (Dev project); other
// previews have no key and stay silent (mode 'live' + no key = off).
// Local dev runs in 'debug' mode — events are logged to the console, never
// sent. Prod routes capture through the same-origin /ingest proxy (see
// vercel.json).
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY ?? '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';
const POSTHOG_UI_HOST = import.meta.env.VITE_POSTHOG_UI_HOST ?? 'https://us.posthog.com';
const POSTHOG_MODE: AnalyticsMode = import.meta.env.DEV ? 'debug' : 'live';

// Build-time commit sha (vite.config.ts `define`), `local` outside Vercel.
declare const __APP_VERSION__: string;

// PostHog `identity` profile: opted in per Vercel project through
// VITE_POSTHOG_PROFILE, never on a white-label build (VITE_BRAND, e.g. Apptics).
// The condition is written inline on the env values so a white-label build
// drops every new option at compile time; anything else keeps today's init.
const POSTHOG_PROFILE_OPTIONS =
  !import.meta.env.VITE_BRAND && profileFromEnv(import.meta.env.VITE_POSTHOG_PROFILE) === 'identity'
    ? {
        profile: 'identity' as const,
        app: 'crm_v2' as const,
        // `typeof` never throws, so a build without the define still boots.
        appVersion: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'local',
        // `useAnalyticsIdentity` (App root) releases it once Clerk has loaded without an actor.
        awaitAuth: true,
      }
    : {};

const satellite = resolveSatellite();
const isVercelPreview = typeof window !== 'undefined' && window.location.hostname.endsWith('.vercel.app');

// Partner origins: only the partners this page knows (own host partner, or the
// sign-in bounce's partner on a Tagada host), never the whole registry.
const allowedRedirectOrigins = () => [
  PRIMARY_APP_ORIGIN,
  ...partnerOrigins(),
  ...TAGADAPAY_SATELLITE_ORIGINS,
  ...(isVercelPreview
    ? [
        window.location.origin,
        import.meta.env.VITE_CRM_URL || PRIMARY_APP_ORIGIN,
      ]
    : []),
];

// Clerk's ClerkProviderProps discriminated union requires isSatellite+domain to
// be passed together (or omitted together). Spread conditionally to satisfy TS.
//
// `signInUrl` MUST point to a different origin than the satellite - Clerk
// enforces this at runtime. Pointing it at the primary sends users there to
// authenticate, then back with a session cookie scoped to the satellite.
const satelliteProps = satellite
  ? {
      isSatellite: true as const,
      domain: satellite.domain,
      signInUrl: `${PRIMARY_APP_ORIGIN}/sign-in`,
    }
  : {};

const render = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        allowedRedirectOrigins={allowedRedirectOrigins()}
        {...satelliteProps}
      >
        <AnalyticsProvider
          apiKey={POSTHOG_KEY}
          apiHost={POSTHOG_HOST}
          uiHost={POSTHOG_UI_HOST}
          mode={POSTHOG_MODE}
          sanitizeUrls
          {...POSTHOG_PROFILE_OPTIONS}
        >
          <NuqsAdapter>
            <BrowserRouter>
              <GaPageViews />
              <App />
            </BrowserRouter>
          </NuqsAdapter>
        </AnalyticsProvider>
      </ClerkProvider>
    </StrictMode>,
  );

// A `#/?redirect_url=` sign-in bounce is invisible to the middleware: resolve
// its partner before the first render (never rejects, bounded by a timeout).
void resolveRedirectPartnerFromHash().finally(render);
